// =====================================================================
// Heartbeat helpers (product rule 6). "Last activity X" is the newest of
// entry created/edited, update added, or link added. The backend will
// expose folder.last_activity_at (contract), but we also compute it
// client-side from the loaded entries/links so the overview and folder
// header stay correct even before that field lands, and so the folder
// detail can recompute live after an edit without a refetch.
//
// Also: the stale-Waiting attention state — a folder with a Waiting
// entry older than N days (default 7, configurable) gets an attention
// marker on the overview.
// =====================================================================

import { normalizeStatus, STATUS } from "../constants/taskboardStatus";

export const STALE_WAITING_DAYS = 7;

function toTime(value) {
  if (!value) return 0;
  const t = new Date(value).getTime();
  return Number.isNaN(t) ? 0 : t;
}

// Newest activity timestamp (ms) across a folder + its entries + links.
// Any of the arguments may be undefined. Falls back to 0 when nothing
// is known.
export function lastActivityMs(folder, entries = [], links = []) {
  let newest = toTime(folder?.last_activity_at);
  for (const e of entries || []) {
    newest = Math.max(newest, toTime(e?.updated_at), toTime(e?.created_at), toTime(e?.date));
    for (const u of e?.updates || []) {
      newest = Math.max(newest, toTime(u?.created_at));
    }
  }
  for (const l of links || []) {
    newest = Math.max(newest, toTime(l?.created_at), toTime(l?.updated_at));
  }
  return newest;
}

// Compact relative label matching the design ("just now", "2h ago",
// "3d ago", "Jun 14"). Pure + injectable `now` for tests.
export function relativeActivity(ms, now = Date.now()) {
  if (!ms) return "No activity yet";
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hrs = Math.floor(min / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  const d = new Date(ms);
  // English UI copy per repo i18n conventions — pin the locale so the
  // date does not follow the host machine's locale (e.g. German).
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

// Max age (in whole days) among a folder's stale Waiting entries, or 0.
// Drives the overview "Waiting Nd" attention pill.
export function staleWaitingDays(entries = [], thresholdDays = STALE_WAITING_DAYS, now = Date.now()) {
  let max = 0;
  for (const e of entries || []) {
    if (normalizeStatus(e?.status) !== STATUS.WAITING) continue;
    const age = entryAgeDays(e, now);
    if (age != null && age >= thresholdDays) max = Math.max(max, age);
  }
  return max;
}

// Does the folder have a Waiting entry older than the threshold? Used
// for the overview attention state. `entries` optional — when the
// overview only has summary counts, callers pass folder-level hints.
export function hasStaleWaiting(entries = [], thresholdDays = STALE_WAITING_DAYS, now = Date.now()) {
  const cutoff = now - thresholdDays * 24 * 60 * 60 * 1000;
  return (entries || []).some((e) => {
    if (normalizeStatus(e?.status) !== STATUS.WAITING) return false;
    const t = toTime(e?.updated_at) || toTime(e?.created_at) || toTime(e?.date);
    return t > 0 && t < cutoff;
  });
}

// Age in whole days of an entry (for the "9d" aging hint on Waiting
// cards). Returns null when no timestamp is known.
export function entryAgeDays(entry, now = Date.now()) {
  const t = toTime(entry?.updated_at) || toTime(entry?.created_at) || toTime(entry?.date);
  if (!t) return null;
  return Math.floor((now - t) / (24 * 60 * 60 * 1000));
}
